// Event validation utilities — aligned with nssc-2026 frontend shape
// (src/lib/data/events-data.json). See EventForm.js for the UI that consumes
// `eventFields`.

export function validateEventData(eventData) {
    const errors = [];

    if (!eventData.slug || eventData.slug.trim() === '') {
        errors.push('Slug is required.');
    } else if (!/^[a-z][a-z0-9-]*$/.test(eventData.slug)) {
        errors.push('Slug must be lowercase letters, digits or hyphens, and start with a letter (e.g. "space-quiz").');
    }

    if (!eventData.name || eventData.name.trim() === '') {
        errors.push('Event name is required.');
    }

    if (eventData.backendId == null || isNaN(parseInt(eventData.backendId, 10))) {
        errors.push('Backend ID is required (numeric — must match /nssc_backend/data/events.json).');
    } else if (parseInt(eventData.backendId, 10) < 1) {
        errors.push('Backend ID must be 1 or greater.');
    }

    if (!eventData.shortDescription || eventData.shortDescription.trim() === '') {
        errors.push('Short description is required.');
    } else if (eventData.shortDescription.length > 180) {
        errors.push('Short description must be under 180 characters.');
    }

    if (!eventData.description || eventData.description.trim() === '') {
        errors.push('Long description is required.');
    }

    if (!eventData.submissiondate || !isValidDate(eventData.submissiondate)) {
        errors.push('A valid submission date (YYYY-MM-DD) is required.');
    }

    if (!eventData.prize || eventData.prize.trim() === '') {
        errors.push('Prize is required.');
    }

    if (!eventData.rules || !Array.isArray(eventData.rules) || eventData.rules.length === 0) {
        errors.push('At least one rule is required.');
    }

    // Team event guards — mirrors the backend + frontend registration checks.
    if (eventData.team === true) {
        const min = parseInt(eventData.min, 10);
        const max = parseInt(eventData.max, 10);
        if (isNaN(min) || min < 1) {
            errors.push('Minimum team size must be a number >= 1 for team events.');
        }
        if (isNaN(max) || max < 1) {
            errors.push('Maximum team size must be a number >= 1 for team events.');
        }
        if (!isNaN(min) && !isNaN(max) && min > max) {
            errors.push('Minimum team size cannot exceed maximum.');
        }
    }

    // Contact — expected: array of { name, number }, at least one entry.
    if (eventData.contact) {
        if (!Array.isArray(eventData.contact)) {
            errors.push('Contact must be a list.');
        } else {
            eventData.contact.forEach((c, i) => {
                if (!c || typeof c !== 'object') {
                    errors.push(`Contact ${i + 1}: expected {name, number}.`);
                } else {
                    if (!c.name || String(c.name).trim() === '') errors.push(`Contact ${i + 1}: name is required.`);
                    if (!c.number || String(c.number).trim() === '') errors.push(`Contact ${i + 1}: number is required.`);
                }
            });
        }
    }

    if (eventData.psLink && String(eventData.psLink).trim() !== '' && !isValidURL(eventData.psLink)) {
        errors.push('Problem-statement link must be a valid URL.');
    }

    if (eventData.elink && String(eventData.elink).trim() !== '' && !isValidURL(eventData.elink)) {
        errors.push('External link must be a valid URL.');
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}

function isValidDate(dateString) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateString.match(regex)) return false;
    const date = new Date(dateString);
    const timestamp = date.getTime();
    if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) return false;
    return dateString === date.toISOString().split('T')[0];
}

function isValidURL(string) {
    try {
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
}

export function sanitizeEventData(eventData) {
    const sanitized = { ...eventData };

    ['slug', 'name', 'shortDescription', 'description', 'prize', 'psLink', 'elink', 'submissiondate'].forEach((field) => {
        if (typeof sanitized[field] === 'string') {
            sanitized[field] = sanitized[field].trim();
        }
    });

    if (typeof sanitized.slug === 'string') {
        sanitized.slug = sanitized.slug.toLowerCase();
    }

    sanitized.team = Boolean(sanitized.team);
    sanitized.registrationStarted = Boolean(sanitized.registrationStarted);

    if (sanitized.backendId != null && sanitized.backendId !== '') {
        sanitized.backendId = parseInt(sanitized.backendId, 10);
    }

    if (sanitized.team) {
        sanitized.min = sanitized.min != null && sanitized.min !== '' ? parseInt(sanitized.min, 10) : null;
        sanitized.max = sanitized.max != null && sanitized.max !== '' ? parseInt(sanitized.max, 10) : null;
    } else {
        sanitized.min = null;
        sanitized.max = null;
    }

    if (Array.isArray(sanitized.rules)) {
        sanitized.rules = sanitized.rules.map((r) => String(r).trim()).filter((r) => r !== '');
    }

    if (Array.isArray(sanitized.contact)) {
        sanitized.contact = sanitized.contact
            .filter((c) => c && (c.name || c.number))
            .map((c) => ({
                name: String(c.name || '').trim(),
                number: String(c.number || '').trim(),
            }));
    } else {
        sanitized.contact = [];
    }

    return sanitized;
}

// Field definitions consumed by EventForm.js. Anything with `custom: true` is
// rendered by hand in EventForm (rules textarea, contact list); the rest go
// through the generic renderer.
export const eventFields = [
    {
        name: 'slug',
        label: 'Slug',
        type: 'text',
        required: true,
        placeholder: 'e.g. astrophotography',
        helpText: 'URL identifier. Lowercase letters, digits, hyphens. Starts with a letter.',
    },
    {
        name: 'name',
        label: 'Event Name',
        type: 'text',
        required: true,
        placeholder: 'e.g. ASTROPHOTOGRAPHY',
    },
    {
        name: 'backendId',
        label: 'Backend ID',
        type: 'number',
        required: true,
        placeholder: 'e.g. 1',
        helpText: 'Must match the id in nssc_backend/data/events.json. 1-3 = individual, 4+ = team.',
    },
    {
        name: 'shortDescription',
        label: 'Short Description',
        type: 'textarea',
        required: true,
        placeholder: 'One-line teaser shown on the /events grid and profile chips…',
        helpText: 'Max 180 characters. Shown wherever a compact summary is needed.',
    },
    {
        name: 'description',
        label: 'Long Description',
        type: 'textarea',
        required: true,
        placeholder: 'Full description shown on the event details page…',
        helpText: 'Full body copy for the detail page.',
    },
    {
        name: 'rules',
        label: 'Rules',
        type: 'textarea',
        required: true,
        placeholder: 'Enter each rule on a new line…',
        helpText: 'One rule per line.',
        custom: true,
    },
    {
        name: 'submissiondate',
        label: 'Submission / Registration Deadline',
        type: 'date',
        required: true,
    },
    {
        name: 'registrationStarted',
        label: 'Registration Started',
        type: 'checkbox',
        required: false,
        helpText: 'Uncheck to hide the register button on the site and reject registrations at the API.',
    },
    {
        name: 'prize',
        label: 'Prize',
        type: 'text',
        required: true,
        placeholder: 'e.g. Rs. 10,000',
    },
    {
        name: 'team',
        label: 'Team Event',
        type: 'checkbox',
        required: false,
    },
    {
        name: 'min',
        label: 'Minimum Team Size',
        type: 'number',
        required: false,
        dependsOn: 'team',
        placeholder: 'e.g. 1',
    },
    {
        name: 'max',
        label: 'Maximum Team Size',
        type: 'number',
        required: false,
        dependsOn: 'team',
        placeholder: 'e.g. 4',
    },
    {
        name: 'psLink',
        label: 'Problem Statement Link',
        type: 'url',
        required: false,
        placeholder: 'https://...',
        helpText: 'Optional — shows a "Problem Statement" button on the event page when set.',
    },
    {
        name: 'elink',
        label: 'External Link',
        type: 'url',
        required: false,
        placeholder: 'https://unstop.com/...',
        helpText: 'Optional — e.g. Unstop mirror or external submission page.',
    },
    {
        name: 'contact',
        label: 'Contact',
        type: 'contactList',
        required: false,
        helpText: 'One or two people participants can reach.',
        custom: true,
    },
];
